// Phase 0085 — Escalation Inbox.
// Shows escalations addressed to roles the current user holds.

import { App, Button, Empty, Form, Input, Modal, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { taskApi, type EscalationRow } from '@/features/hr/taskExecutionApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

export default function EscalationInboxPage() {
  const { message } = App.useApp()
  const [resolveOpen, setResolveOpen] = useState<EscalationRow | null>(null)
  const [form] = Form.useForm()

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['escalation-inbox'],
    queryFn: () => taskApi.escalationInbox('OPEN'),
    refetchInterval: 30000, // refresh every 30s
  })

  const ackMut = useMutation({
    mutationFn: (r: EscalationRow) => taskApi.ackEscalation(r.id),
    onSuccess: () => { message.success('Acknowledged'); refetch() },
    onError: (e: Error) => message.error(e.message),
  })
  const resolveMut = useMutation({
    mutationFn: ({ r, remarks }: { r: EscalationRow; remarks: string }) =>
      taskApi.resolveEscalation(r.id, remarks),
    onSuccess: () => {
      message.success('Resolved'); setResolveOpen(null); form.resetFields(); refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const columns: ColumnsType<EscalationRow> = [
    { title: 'Level', dataIndex: 'escalation_level', width: 80,
      render: (l: number) => <Tag color={l === 3 ? 'red' : l === 2 ? 'volcano' : 'orange'}>L{l}</Tag> },
    { title: 'Task ID', dataIndex: 'task_instance_id', width: 260,
      render: (id: string) => <Text code style={{ fontSize: 11 }}>{id}</Text> },
    { title: 'Triggered', dataIndex: 'triggered_at', width: 160,
      render: (d: string) => dayjs(d).format('MMM D HH:mm') },
    { title: 'Status', dataIndex: 'resolution_status', width: 130,
      render: (s: string) => <Tag>{s}</Tag> },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (_, r) => (
        <Space size={4}>
          {!r.ack_at && (
            <Button size="small" onClick={() => ackMut.mutate(r)}>Acknowledge</Button>
          )}
          <Button size="small" type="primary"
                  onClick={() => { setResolveOpen(r); form.resetFields() }}>
            Resolve
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Escalation Inbox"
        subtitle="Tasks overdue and escalated to roles you hold"
        extra={<Button onClick={() => refetch()}>Refresh</Button>}
      />
      {rows.length === 0 ? (
        <Empty description="Inbox empty. All clear." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={rows}
               loading={isLoading} pagination={{ pageSize: 25 }} />
      )}

      <Modal
        open={!!resolveOpen}
        title="Resolve escalation"
        onCancel={() => setResolveOpen(null)}
        onOk={() => form.submit()}
        confirmLoading={resolveMut.isPending}
      >
        <Form form={form} layout="vertical"
              onFinish={(v: { remarks: string }) =>
                resolveOpen && resolveMut.mutate({ r: resolveOpen, remarks: v.remarks })}>
          <Form.Item name="remarks" label="Resolution notes" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="What did you do to resolve this?" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
